import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Radio,
  Checkbox,
  Collapse,
  IconButton,
  Typography,
  TableSortLabel,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import Loading from "../../Loading/Loading";
import SearchFilter from "../CommonComponent/SearchFilter";
import useHttp from "../../Hooks/use-http";
import Modal from "../../GeneratePDF/Modal";
import { toast } from "react-toastify";
import {
  PAGINATIONBASEVALUE,
  USEREMAILIDFROMLOCALSTORAGE,
} from "../../Constant";
import DefaultImage from "../../Assets/default_image.jpg";
import { useLocation } from "react-router-dom";
import formatDateTime from "../CommonComponent/timeFormatT";
import dayjs from "dayjs";
import DateRangePickerWithFilter from "../CommonComponent/DateFilterByMUI";
import { FormatDateTime } from "../CommonComponent/DateFormat";
import CommonTablePagination from "../CommonComponent/CommonTablePagination";
import FuelExportComponent from "../Vechicles/FuelExportComponent";
import PortalTitle from "../CommonComponent/PortalTitle";
import { usePageTitle } from "../../Context/PageTitleContext";

const Fuels = () => {
  const { setPageTitle } = usePageTitle();
  const [openAddFuelModal, setOpenAddFuelModal] = useState(false);
  const [fuels, setFuels] = useState([]);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(PAGINATIONBASEVALUE);
  const [selectedFuel, setSelectedFuel] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [mainData, setMainData] = useState(null);
  const { isLoading, sendRequest } = useHttp();
  const [isExportButtonClicked, setIsExportButtonClicked] = useState(false);
  const [isExportForExcelButtonClicked, setIsExportForExcelButtonClicked] =
    useState(false);
  const [expandedRows, setExpandedRows] = useState({});
  const emailID = localStorage.getItem("user");
  const [loader, setLoader] = useState(false);
  const [getVehiclesFromVehicles, setGetVehiclesFromVehicles] = useState(null);
  const location = useLocation();
  const [startDateValue, setStartDateValue] = useState(dayjs(new Date()));
  const [endDateValue, setEndDateValue] = useState(dayjs(new Date()));
  const [isRefreshData, setIsRefereshData] = useState(false);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("regNumber");
  const [totalPetrol, setTotalPetrol] = useState(0);
  const [totalDiesel, setTotalDiesel] = useState(0);
  const [totalCNG, setTotalCNG] = useState(0);
  const [totalBill, setTotalBill] = useState(0);
  const [filteredData, setFilteredData] = useState([]);
  const [editingField, setEditingField] = useState({
    fuelId: null,
    fieldId: null,
  });
  const [currentEditValue, setCurrentEditValue] = useState("");

  // Check for mobile screen size
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    setPageTitle("Fuels");
  }, [setPageTitle]);

  useEffect(() => {
    if (location.state && location.state.data) {
      setGetVehiclesFromVehicles(location.state.data);
    }
  }, [location.state]);

  const toggleRowExpand = (id) => {
    setExpandedRows((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleRowClick = (id) => {
    toggleRowExpand(id);
  };

  const toggleEditField = (fuelId, fieldId, currentValue) => {
    setEditingField((prev) =>
      prev.fuelId === fuelId && prev.fieldId === fieldId
        ? { fuelId: null, fieldId: null }
        : { fuelId, fieldId }
    );
    setCurrentEditValue(currentValue);
  };

  const handleSubmitEdit = async (fuelId, fieldId) => {
    try {
      setLoader(true);

      // Find the fuel entry being edited
      const fuel = fuels.find((f) => f.ID === fuelId);

      // Parse the original taskDetails
      const originalDetails = parseTaskDetails(fuel.taskDetails);

      // Create updated details by mapping through original fields
      const updatedDetails = originalDetails.map((field) => {
        if (field.FieldID === fieldId) {
          return {
            ...field,
            Answer: currentEditValue,
          };
        }
        return field;
      });

      // Prepare the payload with ALL fields preserved
      const payload = {
        ...fuel,
        taskDetails: JSON.stringify(updatedDetails),
        modifiedBy: localStorage.getItem("user"),
        modifiedOn: new Date().toISOString(),
      };

      // Call your API to update the fuel entry
      sendRequest(
        {
          url: "/fuelUpdate",
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: payload,
        },
        (data) => {
          toast.success("Field updated successfully");
          getFuelList();
          setEditingField({ fuelId: null, fieldId: null });
          setCurrentEditValue("");
        },
        (error) => {
          toast.error("Error updating field:", error);
          setLoader(false);
        }
      );
    } catch (error) {
      toast.error("Error updating field:", error);
      setLoader(false);
    }
  };

  const renderField = (field, index, fuelId) => {
    if (!field) return null;
    const { FieldText, FieldType, ExtraDetails, Answer, FieldID, Required } =
      field;
    const isEditing =
      editingField.fuelId === fuelId && editingField.fieldId === FieldID;

    // Function to handle edit button click and stop propagation
    const handleEditClick = (e, fuelId, fieldId, currentValue) => {
      e.stopPropagation(); // Stop event from bubbling up to parent elements
      toggleEditField(fuelId, fieldId, currentValue);
    };

    // Function to handle all edit-related clicks and stop propagation
    const handleEditInteraction = (e) => {
      e.stopPropagation(); // Prevent click from propagating to parent
    };

    // Function to handle text field changes
    const handleTextFieldChange = (e) => {
      e.stopPropagation(); // Stop event propagation
      setCurrentEditValue(e.target.value);
    };

    // Function to handle radio button changes
    const handleRadioChange = (e, valueId) => {
      e.stopPropagation(); // Stop event propagation
      setCurrentEditValue(valueId);
    };

    switch (FieldType) {
      case "INPUT":
      case "NUMBER":
        return (
          <div style={{ marginBottom: "10px" }} onClick={handleEditInteraction}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="subtitle2" style={{ fontWeight: "bold" }}>
                {FieldText}{" "}
                {Required && <span style={{ color: "red" }}>*</span>}
              </Typography>
              {!isEditing ? (
                <IconButton
                  size="small"
                  onClick={(e) => handleEditClick(e, fuelId, FieldID, Answer)}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              ) : null}
            </Box>
            {isEditing ? (
              <Box
                display="flex"
                alignItems="center"
                gap={1}
                onClick={handleEditInteraction}
              >
                <TextField
                  value={currentEditValue || Answer || ""}
                  onChange={handleTextFieldChange}
                  onClick={handleEditInteraction}
                  onKeyDown={handleEditInteraction}
                  fullWidth
                  variant="outlined"
                  size="small"
                  type={FieldType === "NUMBER" ? "number" : "text"}
                  required={Required}
                  autoFocus
                />
                <IconButton
                  color="primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSubmitEdit(fuelId, FieldID);
                  }}
                  disabled={Required && !currentEditValue}
                >
                  <CheckIcon />
                </IconButton>
                <IconButton
                  color="error"
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditingField({ fuelId: null, fieldId: null });
                    setCurrentEditValue("");
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            ) : (
              <TextField
                value={Answer || ""}
                disabled
                fullWidth
                variant="outlined"
                size="small"
                sx={{ backgroundColor: "#f5f5f5" }}
                onClick={handleEditInteraction}
              />
            )}
          </div>
        );

      case "IMAGE":
        return (
          <div style={{ marginBottom: "10px" }} onClick={handleEditInteraction}>
            <Typography variant="subtitle2" style={{ fontWeight: "bold" }}>
              {FieldText} {Required && <span style={{ color: "red" }}>*</span>}
            </Typography>
            {Answer && Answer !== "null" ? (
              <a href={Answer} target="_blank" rel="noopener noreferrer">
                <img
                  src={Answer}
                  alt="Answer"
                  style={{
                    width: "100px",
                    height: "100px",
                    objectFit: "cover",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                  }}
                />
              </a>
            ) : (
              <a href={DefaultImage} target="_blank" rel="noopener noreferrer">
                <img
                  src={DefaultImage}
                  alt="Default"
                  style={{
                    width: "100px",
                    height: "100px",
                    objectFit: "cover",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                  }}
                />
              </a>
            )}
          </div>
        );

      case "MULTI_CHOICE":
        return (
          <div style={{ marginBottom: "10px" }} onClick={handleEditInteraction}>
            <Typography variant="subtitle2" style={{ fontWeight: "bold" }}>
              {FieldText} {Required && <span style={{ color: "red" }}>*</span>}
            </Typography>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
              {ExtraDetails?.FieldValue?.map((value, idx) => (
                <div
                  key={idx}
                  style={{ display: "flex", alignItems: "center" }}
                >
                  <Checkbox
                    checked={Answer?.includes?.(value.ValueID)}
                    disabled
                    size="small"
                    onClick={handleEditInteraction}
                  />
                  <Typography variant="body2">{value.ValueText}</Typography>
                </div>
              ))}
            </div>
          </div>
        );

      case "SINGLE_CHOICE":
        return (
          <div style={{ marginBottom: "10px" }} onClick={handleEditInteraction}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="subtitle2" style={{ fontWeight: "bold" }}>
                {FieldText}{" "}
                {Required && <span style={{ color: "red" }}>*</span>}
              </Typography>
              {!isEditing ? (
                <IconButton
                  size="small"
                  onClick={(e) => handleEditClick(e, fuelId, FieldID, Answer)}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              ) : null}
            </Box>
            {isEditing ? (
              <Box
                display="flex"
                flexDirection="column"
                gap={1}
                onClick={handleEditInteraction}
              >
                <div
                  style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}
                  onClick={handleEditInteraction}
                >
                  {ExtraDetails?.FieldValue?.map((value, idx) => (
                    <div
                      key={idx}
                      style={{ display: "flex", alignItems: "center" }}
                      onClick={handleEditInteraction}
                    >
                      <Radio
                        checked={currentEditValue === value.ValueID}
                        onChange={(e) => handleRadioChange(e, value.ValueID)}
                        onClick={handleEditInteraction}
                        size="small"
                      />
                      <Typography variant="body2">{value.ValueText}</Typography>
                    </div>
                  ))}
                </div>
                <Box display="flex" justifyContent="flex-end" gap={1}>
                  <IconButton
                    color="primary"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSubmitEdit(fuelId, FieldID);
                    }}
                    disabled={Required && !currentEditValue}
                  >
                    <CheckIcon />
                  </IconButton>
                  <IconButton
                    color="error"
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingField({ fuelId: null, fieldId: null });
                      setCurrentEditValue("");
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                </Box>
              </Box>
            ) : (
              <div
                style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}
                onClick={handleEditInteraction}
              >
                {ExtraDetails?.FieldValue?.map((value, idx) => (
                  <div
                    key={idx}
                    style={{ display: "flex", alignItems: "center" }}
                  >
                    <Radio
                      checked={Answer === value.ValueID}
                      disabled
                      size="small"
                      onClick={handleEditInteraction}
                    />
                    <Typography variant="body2">{value.ValueText}</Typography>
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };
  const parseTaskDetails = (taskDetails) => {
    if (!taskDetails) return [];

    try {
      const firstParse = JSON.parse(taskDetails);
      const parsedDetails =
        typeof firstParse === "string" ? JSON.parse(firstParse) : firstParse;

      return Array.isArray(parsedDetails)
        ? parsedDetails
        : Object.values(parsedDetails);
    } catch (error) {
      console.error("Error parsing task details:", error);
      return [];
    }
  };

  const calculateTotals = (data) => {
    if (!data) return;

    let petrol = 0;
    let diesel = 0;
    let cng = 0;
    let bill = 0;

    data.forEach((fuel) => {
      const details = parseTaskDetails(fuel.taskDetails);
      const fuelType = details.find((f) => f.FieldID === "FUEL_TYPE")?.Answer;
      const fuelAmount = details.find(
        (f) => f.FieldID === "PUMP_AFTER_FUEL"
      )?.Answer;
      const billAmount = details.find((f) => f.FieldID === "INVOICE")?.Answer;

      const numericAmount = parseFloat(fuelAmount) || 0;
      const numericBill = parseFloat(billAmount) || 0;

      if (fuelType?.toLowerCase() === "petrol") {
        petrol += numericAmount;
      } else if (fuelType?.toLowerCase() === "diesel") {
        diesel += numericAmount;
      } else if (fuelType?.toLowerCase() === "cng") {
        cng += numericAmount;
      }

      bill += numericBill;
    });

    setTotalPetrol(petrol);
    setTotalDiesel(diesel);
    setTotalCNG(cng);
    setTotalBill(bill);
  };

  const getFuelList = async () => {
    try {
      setLoader(true);

      const requestBody = {
        emailID,
        data: {
          filter: {
            fromDate: `${startDateValue.$d.getFullYear()}/${
              startDateValue.$d.getMonth() + 1
            }/${startDateValue.$d.getDate()}`,
            toDate: `${endDateValue.$d.getFullYear()}/${
              endDateValue.$d.getMonth() + 1
            }/${endDateValue.$d.getDate()}`,
          },
        },
      };

      if (getVehiclesFromVehicles) {
        requestBody.data.regNumber = getVehiclesFromVehicles;
      }

      sendRequest(
        {
          url: "/fuelLists",
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: requestBody,
        },
        (data) => {
          setLoader(false);
          setFuels(data?.fuel || []);
        },
        (error) => {
          toast.error("Error fetching fuel data:", error);
          setLoader(false);
          setFuels([]);
        }
      );
    } catch (error) {
      toast.error("Error fetching fuel data:", error);
      setLoader(false);
      setFuels([]);
    }
  };

  useEffect(() => {
    getFuelList();
  }, [getVehiclesFromVehicles, isRefreshData, startDateValue, endDateValue]);

  useEffect(() => {
    if (fuels && fuels.length > 0) {
      const sortedData = stableSort(fuels, getComparator(order, orderBy));
      setMainData(sortedData);
      setFilteredData(sortedData);
    } else {
      setMainData([]);
      setFilteredData([]);
    }
  }, [fuels, order, orderBy]);

  const visibleRows = useMemo(() => {
    if (!filteredData || filteredData.length === 0) return [];

    return filteredData.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
  }, [filteredData, page, rowsPerPage]);

  useEffect(() => {
    calculateTotals(filteredData);
  }, [filteredData]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleExport = () => {
    toast.success("Exporting fuel data...");
  };

  function descendingComparator(a, b, orderBy) {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
    return 0;
  }

  function getComparator(order, orderBy) {
    return order === "desc"
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  }

  function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) return order;
      return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
  }

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const createSortHandler = (property) => (event) => {
    handleRequestSort(property);
  };

  // Mobile card view component
  const MobileCardView = ({ fuel }) => {
    const details = parseTaskDetails(fuel.taskDetails);
    const odoMeterField = details.find((f) => f.FieldID === "ODOMETER");
    const billAmountField = details.find((f) => f.FieldID === "INVOICE");
    const fuelTypeField = details.find((f) => f.FieldID === "FUEL_TYPE");
    const paymentType = details.find((f) => f.FieldID === "PAYMENT_TYPE");
    const fuelFilled = details.find((f) => f.FieldID === "PUMP_AFTER_FUEL");
    const pumpName = details.find((f) => f.FieldID === "PUMP_NAME");

    return (
      <Paper
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 2,
          backgroundColor: expandedRows[fuel.ID] ? "#f5f5f5" : "white",
        }}
        onClick={() => handleRowClick(fuel.ID)}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">
              {fuel.regNumber || "-"}
            </Typography>
            {([fuel.maker, fuel.model].filter(Boolean).length > 0) && (
              <Typography
                variant="body2"
                sx={{
                  fontSize: "0.6875rem",
                  color: "text.secondary",
                  lineHeight: 1.2,
                }}
              >
                {[fuel.maker, fuel.model]
                  .filter(Boolean)
                  .join(" • ")}
              </Typography>
            )}
            <Typography variant="body2" color="textSecondary">
              <strong>ID:</strong> {fuel.ID || "-"}
            </Typography>
            <Typography variant="body2">
              <strong>Driver:</strong> {fuel.driverName || "-"}
            </Typography>
          </Box>
          <IconButton size="small">
            {expandedRows[fuel.ID] ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>

        <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
          <Typography variant="body2">
            <strong>Odometer:</strong> {odoMeterField?.Answer || "-"}
          </Typography>
          <Typography variant="body2">
            <strong>Fuel:</strong> {fuelFilled?.Answer || "-"} L
          </Typography>
          <Typography variant="body2">
            <strong>Type:</strong> {fuelTypeField?.Answer || "-"}
          </Typography>
          <Typography variant="body2">
            <strong>Bill:</strong> {billAmountField?.Answer || "-"}
          </Typography>
        </Box>

        <Typography variant="caption" display="block" mt={1}>
          <strong>Date:</strong> {formatDateTime(fuel.createdAt) || "-"}
        </Typography>

        {expandedRows[fuel.ID] && (
          <Box mt={2}>
            <Typography variant="h6" gutterBottom>
              Fuel Details
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr",
                gap: 2,
                maxHeight: "400px",
                overflowY: "auto",
                padding: 1,
              }}
            >
              {details.map((field, index) => (
                <Paper
                  key={index}
                  sx={{
                    p: 2,
                    backgroundColor: "#fafafa",
                  }}
                >
                  {renderField(field, index, fuel.ID)}
                </Paper>
              ))}
            </Box>
            <Box textAlign="right" mt={1}>
              <Typography variant="caption">
                {fuel?.modifiedOn ? "Modified On " : "Created on"}
                {formatDateTime(fuel?.updatedAt || fuel?.createdAt)} at{" "}
                <a
                  href={`https://www.google.com/maps?q=${fuel?.location}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {fuel?.location}
                </a>
              </Typography>
            </Box>
          </Box>
        )}
      </Paper>
    );
  };

  return (
    <>
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box sx={{ maxWidth: "100%" }}>
          <div>
            <Box
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Paper
                sx={{
                  width: "100%",
                  mb: 2,
                  borderRadius: "10px",
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    px: 2,
                    pt: 1,
                    pb: 1,
                    borderRadius: "10px",
                    flexDirection: isMobile ? "column" : "row",
                    gap: isMobile ? 1 : 0,
                  }}
                >
                  <Box component="h2" sx={{ m: 0, display: "flex", alignItems: "center" }}>
                    <PortalTitle fallback="Fuels" />
                  </Box>
                  <Box
                    sx={{
                      width: isMobile ? "100%" : "40%",
                      display: "flex",
                      gap: "12px",
                      flexDirection: isMobile ? "column" : "row",
                      alignItems: "center",
                    }}
                  >
                    <SearchFilter
                      setFilteredData={setFilteredData}
                      data={mainData}
                      searchValue={searchValue}
                      setSearchValue={setSearchValue}
                      placeholder={"Search"}
                    />
                    <DateRangePickerWithFilter
                      startDateValue={startDateValue}
                      endDateValue={endDateValue}
                      setStartDateValue={setStartDateValue}
                      setEndDateValue={setEndDateValue}
                      setIsRefereshData={setIsRefereshData}
                      loading={loader}
                    />

                    {getVehiclesFromVehicles ? (
                      <Button
                        variant="outlined"
                        sx={{
                          color: "#1D85C6",
                          border: "1px solid #1D85C6",
                          ":hover": {
                            border: "1px solid #1D85C6",
                            color: "black",
                          },
                        }}
                        size="small"
                        onClick={() => setIsExportButtonClicked(true)}
                      >
                        Export
                      </Button>
                    ) : (
                      <Button
                        variant="outlined"
                        sx={{
                          color: "#1D85C6",
                          border: "1px solid #1D85C6",
                          ":hover": {
                            border: "1px solid #1D85C6",
                            color: "black",
                          },
                        }}
                        size="small"
                        onClick={() => setIsExportForExcelButtonClicked(true)}
                      >
                        Export
                      </Button>
                    )}
                  </Box>
                </Box>

                {isMobile ? (
                  // Mobile Card View
                  <Box sx={{ p: 2 }}>
                    {loader ? (
                      <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        height="200px"
                      >
                        <Loading datatable="true" />
                      </Box>
                    ) : visibleRows?.length === 0 ? (
                      <Box textAlign="center" mt={2}>
                        No Data Available
                      </Box>
                    ) : (
                      visibleRows.map((fuel) => (
                        <MobileCardView key={fuel.ID} fuel={fuel} />
                      ))
                    )}
                  </Box>
                ) : (
                  // Desktop Table View
                  <TableContainer
                    component={Paper}
                    sx={{
                      borderRadius: "10px",
                      overflowX: "hidden",
                      width: "100%",
                      boxShadow: "none",
                    }}
                  >
                    <Table
                      aria-label="simple table"
                      sx={{
                        fontFamily: "Montserrat",
                        tableLayout: "fixed",
                        width: "100%",
                      }}
                      size="small"
                    >
                      <TableHead>
                        <TableRow>
                          <TableCell
                            align="center"
                            sx={{
                              width: "5%",
                              fontWeight: 600,
                              fontSize: "0.8125rem",
                              py: 0.5,
                              px: 0.5,
                              color: "rgba(0, 0, 0, 0.87)",
                            }}
                          >
                            {/* Empty header for expand icon */}
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{
                              width: "5%",
                              fontWeight: 600,
                              fontSize: "0.8125rem",
                              py: 0.5,
                              px: 0.5,
                              color: "rgba(0, 0, 0, 0.87)",
                            }}
                            sortDirection={orderBy === "ID" ? order : false}
                          >
                            <TableSortLabel
                              active={orderBy === "ID"}
                              direction={orderBy === "ID" ? order : "asc"}
                              onClick={createSortHandler("ID")}
                              sx={{ fontSize: "0.8125rem" }}
                            >
                              ID
                            </TableSortLabel>
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{
                              width: "9%",
                              fontWeight: 600,
                              fontSize: "0.8125rem",
                              py: 0.5,
                              px: 0.5,
                              color: "rgba(0, 0, 0, 0.87)",
                            }}
                            sortDirection={
                              orderBy === "regNumber" ? order : false
                            }
                          >
                            <TableSortLabel
                              active={orderBy === "regNumber"}
                              direction={
                                orderBy === "regNumber" ? order : "asc"
                              }
                              onClick={createSortHandler("regNumber")}
                              sx={{ fontSize: "0.8125rem" }}
                            >
                              Vehicle
                            </TableSortLabel>
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{
                              width: "9%",
                              fontWeight: 600,
                              fontSize: "0.8125rem",
                              py: 0.5,
                              px: 0.5,
                              color: "rgba(0, 0, 0, 0.87)",
                            }}
                            sortDirection={orderBy === "driver" ? order : false}
                          >
                            <TableSortLabel
                              active={orderBy === "driver"}
                              direction={orderBy === "driver" ? order : "asc"}
                              onClick={createSortHandler("driver")}
                              sx={{ fontSize: "0.8125rem" }}
                            >
                              Driver
                            </TableSortLabel>
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{
                              width: "8%",
                              fontWeight: 600,
                              fontSize: "0.8125rem",
                              py: 0.5,
                              px: 0.5,
                              color: "rgba(0, 0, 0, 0.87)",
                            }}
                            sortDirection={
                              orderBy === "odometer" ? order : false
                            }
                          >
                            <TableSortLabel
                              active={orderBy === "odometer"}
                              direction={orderBy === "odometer" ? order : "asc"}
                              onClick={createSortHandler("odometer")}
                              sx={{ fontSize: "0.8125rem" }}
                            >
                              Odometer
                            </TableSortLabel>
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{
                              width: "9%",
                              fontWeight: 600,
                              fontSize: "0.8125rem",
                              py: 0.5,
                              px: 0.5,
                              color: "rgba(0, 0, 0, 0.87)",
                            }}
                            sortDirection={
                              orderBy === "pumpName" ? order : false
                            }
                          >
                            <TableSortLabel
                              active={orderBy === "pumpName"}
                              direction={orderBy === "pumpName" ? order : "asc"}
                              onClick={createSortHandler("pumpName")}
                              sx={{ fontSize: "0.8125rem" }}
                            >
                              Pump Name
                            </TableSortLabel>
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{
                              width: "8%",
                              fontWeight: 600,
                              fontSize: "0.8125rem",
                              py: 0.5,
                              px: 0.5,
                              color: "rgba(0, 0, 0, 0.87)",
                            }}
                            sortDirection={
                              orderBy === "fuelType" ? order : false
                            }
                          >
                            <TableSortLabel
                              active={orderBy === "fuelType"}
                              direction={orderBy === "fuelType" ? order : "asc"}
                              onClick={createSortHandler("fuelType")}
                              sx={{ fontSize: "0.8125rem" }}
                            >
                              Fuel Type
                            </TableSortLabel>
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{
                              width: "7%",
                              fontWeight: 600,
                              fontSize: "0.8125rem",
                              py: 0.5,
                              px: 0.5,
                              color: "rgba(0, 0, 0, 0.87)",
                            }}
                            sortDirection={
                              orderBy === "fuelFilled" ? order : false
                            }
                          >
                            <TableSortLabel
                              active={orderBy === "fuelFilled"}
                              direction={
                                orderBy === "fuelFilled" ? order : "asc"
                              }
                              onClick={createSortHandler("fuelFilled")}
                              sx={{ fontSize: "0.8125rem" }}
                            >
                              Fuel
                            </TableSortLabel>
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{
                              width: "10%",
                              fontWeight: 600,
                              fontSize: "0.8125rem",
                              py: 0.5,
                              px: 0.5,
                              color: "rgba(0, 0, 0, 0.87)",
                            }}
                            sortDirection={
                              orderBy === "paymentType" ? order : false
                            }
                          >
                            <TableSortLabel
                              active={orderBy === "paymentType"}
                              direction={
                                orderBy === "paymentType" ? order : "asc"
                              }
                              onClick={createSortHandler("paymentType")}
                              sx={{ fontSize: "0.8125rem" }}
                            >
                              Payment Type
                            </TableSortLabel>
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{
                              width: "8%",
                              fontWeight: 600,
                              fontSize: "0.8125rem",
                              py: 0.5,
                              px: 0.5,
                              color: "rgba(0, 0, 0, 0.87)",
                            }}
                            sortDirection={
                              orderBy === "billAmount" ? order : false
                            }
                          >
                            <TableSortLabel
                              active={orderBy === "billAmount"}
                              direction={
                                orderBy === "billAmount" ? order : "asc"
                              }
                              onClick={createSortHandler("billAmount")}
                              sx={{ fontSize: "0.8125rem" }}
                            >
                              Bill
                            </TableSortLabel>
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{
                              width: "12%",
                              fontWeight: 600,
                              fontSize: "0.8125rem",
                              py: 0.5,
                              px: 0.5,
                              color: "rgba(0, 0, 0, 0.87)",
                            }}
                            sortDirection={
                              orderBy === "createdOn" ? order : false
                            }
                          >
                            <TableSortLabel
                              active={orderBy === "createdOn"}
                              direction={
                                orderBy === "createdOn" ? order : "asc"
                              }
                              onClick={createSortHandler("createdOn")}
                              sx={{ fontSize: "0.8125rem" }}
                            >
                              Date
                            </TableSortLabel>
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{
                              width: "12%",
                              fontWeight: 600,
                              fontSize: "0.8125rem",
                              py: 0.5,
                              px: 0.5,
                              color: "rgba(0, 0, 0, 0.87)",
                            }}
                            sortDirection={
                              orderBy === "approvedBy" ? order : false
                            }
                          >
                            <TableSortLabel
                              active={orderBy === "approvedBy"}
                              direction={
                                orderBy === "approvedBy" ? order : "asc"
                              }
                              onClick={createSortHandler("approvedBy")}
                              sx={{ fontSize: "0.8125rem" }}
                            >
                              OTP Verified By
                            </TableSortLabel>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      {loader ? (
                        <TableBody>
                          <TableRow>
                            <TableCell colSpan={12} align="center">
                              <Loading datatable="true" />
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      ) : visibleRows?.length === 0 ? (
                        <TableBody>
                          <TableRow>
                            <TableCell colSpan={12} align="center">
                              <div
                                style={{
                                  textAlign: "center",
                                  marginTop: "10px",
                                }}
                              >
                                No Data Available
                              </div>
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      ) : (
                        <TableBody>
                          {visibleRows?.map((fuel) => {
                            const details = parseTaskDetails(fuel.taskDetails);

                            const odoMeterField = details.find(
                              (f) => f.FieldID === "ODOMETER"
                            );
                            const billAmountField = details.find(
                              (f) => f.FieldID === "INVOICE"
                            );
                            const fuelTypeField = details.find(
                              (f) => f.FieldID === "FUEL_TYPE"
                            );
                            const paymentType = details.find(
                              (f) => f.FieldID === "PAYMENT_TYPE"
                            );
                            const fuelFilled = details.find(
                              (f) => f.FieldID === "PUMP_AFTER_FUEL"
                            );
                            const pumpName = details.find(
                              (f) => f.FieldID === "PUMP_NAME"
                            );

                            return (
                              <React.Fragment key={fuel.ID}>
                                <TableRow
                                  hover
                                  onClick={() => handleRowClick(fuel.ID)}
                                  style={{ cursor: "pointer" }}
                                  sx={{ height: "auto" }}
                                >
                                  <TableCell
                                    align="center"
                                    sx={{
                                      fontSize: "0.8125rem",
                                      py: 0.5,
                                      px: 0.5,
                                    }}
                                  >
                                    <IconButton
                                      size="small"
                                      style={{ padding: "2px" }}
                                    >
                                      {expandedRows[fuel.ID] ? (
                                        <ExpandLess />
                                      ) : (
                                        <ExpandMore />
                                      )}
                                    </IconButton>
                                  </TableCell>
                                  <TableCell
                                    align="center"
                                    sx={{
                                      fontSize: "0.8125rem",
                                      py: 0.5,
                                      px: 0.5,
                                    }}
                                  >
                                    {fuel.ID || "-"}
                                  </TableCell>
                                  {/* <TableCell
                                    align="center"
                                    sx={{
                                      fontSize: "0.8125rem",
                                      py: 0.5,
                                      px: 0.5,
                                    }}
                                  >
                                    {fuel.regNumber || "-"}
                                  </TableCell> */}
                                  <TableCell
                                    align="center"
                                    sx={{
                                      py: 0.5,
                                      px: 0.5,
                                    }}
                                  >
                                    {/* Main text */}
                                    <Typography
                                      sx={{
                                        fontSize: "0.8125rem",
                                        fontWeight: 500,
                                        lineHeight: 1.2,
                                      }}
                                    >
                                      {fuel.regNumber || "-"}
                                    </Typography>

                                    {/* Smaller details */}
                                    <Typography
                                      sx={{
                                        fontSize: "0.6875rem", // smaller
                                        color: "text.secondary",
                                        lineHeight: 1.2,
                                      }}
                                    >
                                      {[fuel.maker, fuel.model]
                                        .filter(Boolean)
                                        .join(" • ")}
                                    </Typography>
                                  </TableCell>

                                  <TableCell
                                    align="center"
                                    sx={{
                                      fontSize: "0.8125rem",
                                      py: 0.5,
                                      px: 0.5,
                                    }}
                                  >
                                    {fuel.driverName || "-"}
                                  </TableCell>
                                  <TableCell
                                    align="center"
                                    sx={{
                                      fontSize: "0.8125rem",
                                      py: 0.5,
                                      px: 0.5,
                                    }}
                                  >
                                    {odoMeterField?.Answer || "-"}
                                  </TableCell>
                                  <TableCell
                                    align="center"
                                    sx={{
                                      fontSize: "0.8125rem",
                                      py: 0.5,
                                      px: 0.5,
                                    }}
                                  >
                                    {pumpName?.Answer || "-"}
                                  </TableCell>
                                  <TableCell
                                    align="center"
                                    sx={{
                                      fontSize: "0.8125rem",
                                      py: 0.5,
                                      px: 0.5,
                                    }}
                                  >
                                    {fuelTypeField?.Answer || "-"}
                                  </TableCell>
                                  <TableCell
                                    align="center"
                                    sx={{
                                      fontSize: "0.8125rem",
                                      py: 0.5,
                                      px: 0.5,
                                    }}
                                  >
                                    {fuelFilled?.Answer || "-"}
                                  </TableCell>
                                  <TableCell
                                    align="center"
                                    sx={{
                                      fontSize: "0.8125rem",
                                      py: 0.5,
                                      px: 0.5,
                                    }}
                                  >
                                    {paymentType?.Answer || "-"}
                                  </TableCell>
                                  <TableCell
                                    align="center"
                                    sx={{
                                      fontSize: "0.8125rem",
                                      py: 0.5,
                                      px: 0.5,
                                    }}
                                  >
                                    {billAmountField?.Answer || "-"}
                                  </TableCell>
                                  <TableCell
                                    align="center"
                                    sx={{
                                      fontSize: "0.8125rem",
                                      py: 0.5,
                                      px: 0.5,
                                    }}
                                  >
                                    {formatDateTime(fuel.createdAt) || "-"}
                                  </TableCell>
                                  <TableCell
                                    align="center"
                                    sx={{
                                      fontSize: "0.8125rem",
                                      py: 0.5,
                                      px: 0.5,
                                    }}
                                  >
                                    {fuel.approvedBy || "-"}
                                  </TableCell>
                                </TableRow>

                                {expandedRows[fuel.ID] && (
                                  <TableRow>
                                    <TableCell
                                      style={{
                                        paddingBottom: 0,
                                        paddingTop: 0,
                                      }}
                                      colSpan={12}
                                    >
                                      <Collapse
                                        in={true}
                                        timeout="auto"
                                        unmountOnExit
                                      >
                                        <Box sx={{ margin: 1 }}>
                                          <Typography
                                            variant="h6"
                                            gutterBottom
                                            component="div"
                                          >
                                            Fuel Details
                                          </Typography>
                                          <div
                                            style={{
                                              display: "grid",
                                              gridTemplateColumns:
                                                "repeat(3, 1fr)",
                                              gap: "12px",
                                              maxHeight: "400px",
                                              overflowY: "auto",
                                              padding: "8px",
                                              width: "100%",
                                            }}
                                          >
                                            {details.map((field, index) => (
                                              <div
                                                key={index}
                                                style={{
                                                  border: "1px solid #eee",
                                                  borderRadius: "8px",
                                                  padding: "12px",
                                                  backgroundColor: "#fafafa",
                                                  maxWidth: "100%",
                                                }}
                                              >
                                                {renderField(
                                                  field,
                                                  index,
                                                  fuel.ID
                                                )}
                                              </div>
                                            ))}
                                          </div>
                                          <div
                                            style={{
                                              display: "flex",
                                              justifyContent: "flex-end",
                                            }}
                                          >
                                            <p style={{ fontSize: "10px" }}>
                                              {fuel?.modifiedOn
                                                ? "Modified On "
                                                : "Created on"}
                                              {formatDateTime(
                                                fuel?.updatedAt ||
                                                  fuel?.createdAt
                                              )}{" "}
                                              at{" "}
                                              <a
                                                style={{ color: "blue" }}
                                                href={`https://www.google.com/maps?q=${fuel?.location}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                              >
                                                {fuel?.location}
                                              </a>
                                            </p>
                                          </div>
                                        </Box>
                                      </Collapse>
                                    </TableCell>
                                  </TableRow>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </TableBody>
                      )}
                    </Table>
                  </TableContainer>
                )}

                <Box
                  sx={{
                    position: "sticky",
                    bottom: 0,
                    background: "white",
                    zIndex: 1,
                    borderTop: "none",
                    border: "none",
                  }}
                >
                  <Box
                    display="flex"
                    sx={{
                      paddingRight: "16px",
                      marginTop: "8px",
                      alignContent: "center",
                      alignItems: "center",
                      display: "flex",
                      justifyContent: "space-between",
                      flexDirection: isMobile ? "column" : "row",
                      gap: isMobile ? 1 : 0,
                      border: "none",
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{
                        margin: "initial",
                        marginLeft: isMobile ? 0 : "2%",
                        textAlign: isMobile ? "center" : "left",
                      }}
                    >
                      <strong style={{ fontSize: "0.8125rem", color: "green" }}>
                        Total Bill:{" "}
                      </strong>
                      <span style={{ fontSize: "0.8rem" }}>
                        ₹{totalBill.toFixed(2)}
                      </span>{" "}
                      <strong style={{ fontSize: "0.8125rem" }}>| </strong>
                      <strong style={{ fontSize: "0.8rem", color: "red" }}>
                        CNG:{" "}
                      </strong>
                      <span style={{ fontSize: "0.8rem" }}>
                        {totalCNG.toFixed(1)} KG
                      </span>
                      <strong style={{ fontSize: "0.8125rem" }}>
                        {" "}
                        | Petrol:{" "}
                      </strong>
                      <span style={{ fontSize: "0.8rem" }}>
                        {totalPetrol.toFixed(1)} L
                      </span>
                      <strong style={{ fontSize: "0.8125rem" }}>
                        {" "}
                        | Diesel:{" "}
                      </strong>
                      <span style={{ fontSize: "0.8rem" }}>
                        {totalDiesel.toFixed(1)} L
                      </span>
                    </Typography>

                    {!isMobile && (
                      <CommonTablePagination
                        page={page}
                        rowsPerPage={rowsPerPage}
                        setRowsPerPage={setRowsPerPage}
                        setPage={setPage}
                        filteredData={filteredData}
                        styling={{
                          display: "flex",
                          justifyContent: "flex-end",
                          border: "none",
                          borderTop: "none",
                          "& .MuiTablePagination-root": {
                            border: "none",
                            borderTop: "none",
                          },
                          "& .MuiTablePagination-toolbar": {
                            border: "none",
                            borderTop: "none",
                          },
                        }}
                      />
                    )}
                  </Box>

                  {isMobile && (
                    <Box 
                      display="flex" 
                      justifyContent="center" 
                      pb={2}
                      sx={{ border: "none", borderTop: "none" }}
                    >
                      <CommonTablePagination
                        page={page}
                        rowsPerPage={rowsPerPage}
                        setRowsPerPage={setRowsPerPage}
                        setPage={setPage}
                        filteredData={filteredData}
                        styling={{
                          border: "none",
                          borderTop: "none",
                          "& .MuiTablePagination-root": {
                            border: "none",
                            borderTop: "none",
                          },
                          "& .MuiTablePagination-toolbar": {
                            border: "none",
                            borderTop: "none",
                          },
                        }}
                      />
                    </Box>
                  )}
                </Box>
              </Paper>
            </Box>
          </div>
          {isExportButtonClicked && (
            <>
              <Modal
                setIsExportButtonClicked={setIsExportButtonClicked}
                regNumber={getVehiclesFromVehicles}
                isPrivateDriver="1"
                isFuel="1"
                type="fuels"
              />
              <div className="add-route-fullcontainer"></div>
            </>
          )}

          {isExportForExcelButtonClicked && (
            <>
              <FuelExportComponent
                setIsExportButtonClicked={setIsExportForExcelButtonClicked}
              />
              <div className="add-route-fullcontainer"></div>
            </>
          )}
        </Box>
      </Box>
    </>
  );
};

export default Fuels;
